// The team sheet of one side in a match of Godsmode, drawn from the game: a
// head with the mark and the name of the side and its energy, then one plate
// for each caster, one over the other, then the pieces the side took. The
// sheet of the player is the desk, where a click on a slot casts. The sheet
// of the other side is the score, small, so the player sees what both sides
// have at all times. `CasterPlate.ts` is the look of a plate.
// Changed 2026-10-12. The plates of the player were in the cue, the plates
// of the other side were in a box with no sheet, and each had a line with
// the word "Energy". Garrett: "on the side are two clearly marked/colored
// sections for the two teams (moon on top, sun on bottom in this case) and
// an updated version of displauing their energy (don't need the \"energy
// label probably\")".
// Changed 2026-10-12. Both sides had four faces of one size in one box, and
// the energy was a row in the log zone. Garrett: "disjoined and hard to see
// energy filling up". Later that day a side got two casters. One plate with
// the faces of both was built first. Rob: "I don't like it as one combined
// bar. I want to see their two bars together, like stacked as individuals."
// A side has one pool of energy, and both casters draw from it. So the pips
// are on a line of their own over the two plates, and no plate has pips. For
// some hours the first plate had them, which read as the energy of that
// caster. Rob: "there should be a single energy pool that both spells draw
// from, rather than leaving individual casters with their own".

import { SLOT_READY_SOUND_MS } from '../constants';
import type { CasterId } from '../content/casters';
import { OWNER_NAMES } from '../engine/describe';
import { otherSide } from '../engine/pieces';
import type { Side } from '../engine/types';
import { canCastSlot, casterGroupsOf, slotIndexGroups, slotsOf } from '../game/casters';
import { energyCapacityOf, type GameState } from '../game/state';
import { el } from '../lib/dom';
import { playSound } from '../sound/player';
import type { SpellId } from '../spells/types';
import { haulStore } from '../state/captureEnergy';
import { chooseCast } from '../state/castChoice';
import { readCaster } from '../state/casterRead';
import { deliveryStore } from '../state/energyDelivery';
import { gameStore } from '../state/game';
import { historyStore, isReviewing } from '../state/history';
import { layoutStore } from '../state/layout';
import { actsFor, localSideStore, spectatingStore } from '../state/localSide';
import { shownEnergy, shownSlots } from '../state/rollShown';
import { stageStore } from '../state/stage';
import { viewedGame } from '../state/view';
import { type CasterPlate, createCasterPlate } from './CasterPlate';
import { createEnergyPips } from './EnergyPips';
import { createGraveyard } from './Graveyard';
import { createLastingSeals } from './LastingSeals';
import { createSideMark } from './SideMark';

/** For each row on the page, the box of the slot that holds a skill. A side has one row in each layout. */
const skillFinders: Record<Side, ((spellId: SpellId) => DOMRect | null)[]> = {
  gold: [],
  violet: [],
};

/**
 * The box on the screen of the slot of `side` that holds the skill `spellId`.
 * The energy a cast pays flies to it. Null when no such slot is on the screen.
 */
export const skillTarget = (side: Side, spellId: SpellId): DOMRect | null => {
  for (const find of skillFinders[side]) {
    const box = find(spellId);
    if (box) return box;
  }
  return null;
};

/** The player may cast from the slot now: it is their turn, and the die and the card are away. */
const isOpenToPlayer = (state: GameState, side: Side, index: number): boolean =>
  actsFor(side) &&
  state.turn === side &&
  !isReviewing() &&
  stageStore.get().phase === 'hidden' &&
  canCastSlot(state, index);

/**
 * The part of a plate that a tap reads on the phone. On the desk it is the
 * symbol and the name, because a tap on a vessel casts. The score has no name
 * on the phone and casts nothing, so it is the whole plate.
 */
const readTargetOf = (plate: CasterPlate): HTMLElement | null =>
  plate.root.classList.contains('caster--score')
    ? plate.root
    : plate.root.querySelector<HTMLElement>('.caster__who');

/** The read target opens the pane of `id` on a tap. With null it is no control: the wide layout has the tooltip. */
const markReadTarget = (target: HTMLElement, id: CasterId | null, name: string): void => {
  target.classList.toggle('caster__read', id !== null);
  if (id === null) {
    target.removeAttribute('role');
    target.removeAttribute('tabindex');
    target.removeAttribute('aria-label');
    target.onclick = null;
    target.onkeydown = null;
    return;
  }
  target.setAttribute('role', 'button');
  target.tabIndex = 0;
  target.ariaLabel = name;
  target.onclick = () => readCaster(id);
  target.onkeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    readCaster(id);
  };
};

/** `variant` is `desk` for the plates the player casts from, and `score` for the plates that are only read. */
export const createCasterRow = (side: Side, variant: 'desk' | 'score'): HTMLElement => {
  // The energy of the side, over the plates of its casters.
  const pips = createEnergyPips(side);
  const energy = el('div', { className: 'casters__energy' }, [
    // The head of the sheet: the mark and the name of the side, then its pool. A plate has the symbol of its caster.
    el('span', { className: 'casters__label' }, [createSideMark(side), OWNER_NAMES[side]]),
    // A spell that binds the side for some turns and changes no piece, such as no energy.
    createLastingSeals(side),
    pips.root,
  ]);
  // The pieces this side took, at the foot of the sheet. A tall window shows them. In a short one they are in the log.
  const took = createGraveyard([otherSide(side)]);
  took.classList.add('casters__took');
  const root = el('section', { className: `casters casters--${variant} casters--${side}` }, [
    energy,
    took,
  ]);
  let plates: CasterPlate[] = [];
  let drawnMatch: GameState['log'][number] | undefined;
  /** The skills the side could pay for at the last draw. */
  let paidSkills = 0;

  // A sheet that is not on the page has no box. Each side has a desk and a score, and a layout mounts one.
  skillFinders[side].push((spellId) => {
    if (!root.isConnected) return null;
    const casters = casterGroupsOf(gameStore.get(), side);
    const slots = slotsOf(gameStore.get(), side);
    const groups = slotIndexGroups(casters);
    for (const [place, indices] of groups.entries()) {
      const local = indices.findIndex((index) => {
        const slot = slots[index];
        return slot?.kind === 'skill' && slot.spellId === spellId;
      });
      if (local >= 0) return plates[place]?.slotBox(local) ?? null;
    }
    return null;
  });

  const draw = (): void => {
    const state = viewedGame();
    const casters = casterGroupsOf(state, side);
    root.hidden = casters.length === 0;
    if (casters.length === 0) return;
    if (plates.length !== casters.length) {
      plates = casters.map(() => createCasterPlate(side, variant));
      root.replaceChildren(energy, ...plates.map((plate) => plate.root), took);
    }
    const reads = layoutStore.get() === 'compact';
    plates.forEach((plate, place) => {
      const id = casters[place]?.caster.id;
      const target = readTargetOf(plate);
      if (target && id)
        markReadTarget(target, reads ? id : null, casters[place]?.caster.name ?? '');
    });
    // The first line of the log is new in each match. A new match fills its pips with no pop.
    const isNewMatch = state.log[0] !== drawnMatch;
    drawnMatch = state.log[0];
    // A scroll from a die that still tumbles is not in its slot yet.
    const slots = shownSlots(state, side);
    const groups = slotIndexGroups(casters);
    const quiet = isNewMatch || isReviewing();
    const shown = shownEnergy(state, side);
    // The row has a pip for each point the pieces of the side can hold.
    pips.draw(shown, quiet, energyCapacityOf(state, side));
    // A coin that drops may pay for a skill. The skill sounds once, after the coin.
    const paidNow = slots.filter(
      (slot) => slot.kind === 'skill' && !slot.locked && slot.cost <= shown,
    ).length;
    if (paidNow > paidSkills && !quiet) playSound('slotReady', SLOT_READY_SOUND_MS);
    paidSkills = paidNow;
    const isTurn = state.turn === side && state.phase !== 'over';
    plates.forEach((plate, place) => {
      const group = casters[place];
      const indices = groups[place] ?? [];
      if (!group) return;
      plate.root.classList.toggle('caster--turn', isTurn);
      // No living piece carries the caster. Its plate stays, with no slot.
      plate.root.classList.toggle('caster--gone', !group.alive);
      plate.draw({
        id: group.caster.id,
        name: group.caster.name,
        slots: indices.flatMap((index) => slots[index] ?? []),
        energy: shown,
        pips: false,
        open: indices.map((index) => isOpenToPlayer(state, side, index)),
        // The plate counts its own slots. The cast needs the place among the slots of the side.
        // A press opens the card of the spell to confirm, see `state/castChoice.ts`.
        onCast:
          variant === 'desk' && actsFor(side)
            ? (local) => {
                const index = indices[local];
                if (index !== undefined) chooseCast(index);
              }
            : null,
        quiet,
      });
    });
  };

  draw();
  gameStore.subscribe(draw);
  stageStore.subscribe(draw);
  // Each coin of a roll that drops gives one energy to the pips and the faces.
  deliveryStore.subscribe(draw);
  // So does each coin of a capture.
  haulStore.subscribe(draw);
  historyStore.subscribe(draw);
  layoutStore.subscribe(draw);
  localSideStore.subscribe(draw);
  spectatingStore.subscribe(draw);
  return root;
};
